"use client";

import React from "react";
import { Clock, CircleQuestionMark as HelpCircle, PhoneCall, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "How fast can an emergency plumber arrive at my Jonesboro home?",
  answer: "We prioritize urgent plumbing situations with local dispatch throughout Jonesboro and surrounding Clayton County communities. Our team typically arrives within 30 to 60 minutes of your emergency call, equipped with the tools and parts needed to halt water damage immediately."
}, {
  id: "item-2",
  question: "What should I do while waiting for the emergency plumber to arrive?",
  answer: "First, shut off your main water supply valve to prevent further flooding. Next, turn off the power to electric water heaters or affected electrical fixtures near standing water. Clear personal items away from the leak area and avoid using any household fixtures until our technician arrives."
}, {
  id: "item-3",
  question: "What qualifies as an urgent plumbing emergency versus a routine repair?",
  answer: "A plumbing emergency is any situation causing active water damage, electrical hazards, complete loss of water, or severe health risks. Examples include ruptured pipes, overflowing sewer backups, leaking water heaters, and frozen supply lines. Routine issues like dripping faucets can usually be scheduled during normal hours."
}, {
  id: "item-4",
  question: "Do you offer transparent pricing for 24/7 emergency dispatch and repairs?",
  answer: "Yes. Before starting any repair work, our licensed plumber assesses the problem on-site and provides a clear, upfront estimate with no hidden surprises. You will know exactly what the diagnostic and repair fees are before we begin work."
}, {
  id: "item-5",
  question: "Can you fix gas line leaks and water heater breakdowns during emergency hours?",
  answer: "Yes. Our technicians handle both gas line emergencies and urgent water heater failures, including tank leaks, pilot light issues, and burst relief valves. If you suspect an active gas leak, leave your property immediately and call emergency services before reaching out to our team."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="emergencyplumbingfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="space-y-12">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <div className="flex justify-center">
              <Badge variant="outline" className="gap-1.5 px-3 py-1 text-sm bg-secondary text-secondary-foreground border-border">
                <HelpCircle className="size-3.5 text-accent" />
                <span>Got Questions?</span>
              </Badge>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Emergency Plumbing FAQs
            </h2>

            <p className="text-muted-foreground text-base md:text-lg">
              Quick answers to urgent plumbing questions for homeowners and businesses in Jonesboro, GA.
            </p>
          </div>

          {/* Accordion Container */}
          <div className="bg-card text-card-foreground rounded-2xl border border-border p-6 md:p-8 shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
              {faqList.map((faq, index) => <motion.div key={faq.id} data-index={index} variants={itemVariants}>
                  <AccordionItem value={faq.id} className="border border-border/70 rounded-xl px-4 py-1 data-[state=open]:bg-muted/40 transition-colors">
                    <AccordionTrigger className="text-base md:text-lg font-semibold text-foreground hover:no-underline py-4">
                      <span className="flex items-center gap-3 text-left">
                        <span className="flex items-center justify-center size-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                          0{index + 1}
                        </span>
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pl-10 pr-2 pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </div>

          {/* Bottom Callout Banner */}
          <motion.div variants={itemVariants} className="rounded-2xl bg-secondary border border-border p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4 text-left">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground shrink-0 shadow-xs">
                <ShieldAlert className="size-6 text-accent" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">
                  Experiencing an active leak or burst pipe right now?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Our dispatch line is staffed 24 hours a day, 7 days a week for immediate local dispatch.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                <a href="tel:4708479076">
                  <PhoneCall className="size-4 text-accent" />
                  <span>Call Emergency Team</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-background hover:bg-muted text-foreground">
                <Link to="/contact/">
                  <Clock className="size-4 mr-1 text-muted-foreground" />
                  <span>Request Service</span>
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}